import {
    AberdeenshireRecordType,
    AberdeenshireRecordSubType
} from "./AberdeenshireRecordTypes";
import { IStyleFactory } from "esdm-map/dist/layers/vector/styles/IStyleFactory";
import Style, { StyleFunction } from "ol/style/Style";
import Fill from 'ol/style/Fill';
import Circle from 'ol/style/Circle';
import Stroke from 'ol/style/Stroke';
import Text from 'ol/style/Text';
import RegularShape from 'ol/style/RegularShape';
import Feature from 'ol/Feature';
import * as olHas from 'ol/has';

export class HbsmrStyleFactory implements IStyleFactory {

    public getStyle(recordType: string, recordSubType: string = null): Style | Style[] | StyleFunction
    {
        let style: Style | Style[] | StyleFunction = null;

        //try and resolve sub type first
        if (recordSubType !== null) {
            switch (recordSubType) {
                case AberdeenshireRecordSubType.ConservationArea:
                    style = this.ConservationArea();
                    break;

                case AberdeenshireRecordSubType.ListedBuilding:
                    style = this.ListedBuilding();
                    break;

                case AberdeenshireRecordSubType.ScheduledMonument:
                    style = this.ScheduledMonument();
                    break;
            
                case AberdeenshireRecordSubType.GardensAndDesignedLandscapes:
                    style = this.GardensAndDesignedLandscapes();
                    break;

                case AberdeenshireRecordSubType.Battlefields:
                    style = this.Battlefields();
                    break;

            }

        }

        if (style === null) {
            switch (recordType) {
                case AberdeenshireRecordType.Monument:
                    style = this.Monument();
                    break;

                case AberdeenshireRecordType.Event:
                    style = this.Event();
                    break;

                case AberdeenshireRecordType.Designation:
                    style = this.Designation();
                    break;
            }
        }

        return style;
    }

    public getSelectedStyle(recordType: AberdeenshireRecordType): Style | Style[] | StyleFunction {
        let style: Style | Style[] | StyleFunction = null;

        switch (recordType) {
            case AberdeenshireRecordType.Monument:
                style = this.MonumentSelected();
                break;

            case AberdeenshireRecordType.Event:
                style = this.EventSelected();
                break;

            case AberdeenshireRecordType.Designation:
                style = this.DesignationSelected();
                break;
        }

        return style;
    }

    private CommonStyle(fillColour: string): Style[] {
        let fill = new Fill({
            color: fillColour
        });
        let stroke = new Stroke({
            color: 'rgba(255,255,255,1)',
            width: 3
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: stroke,
                    radius: 12
                }),
                fill: fill,
                stroke: stroke
            })
        ];
    }


    private Monument(): Style[] {
        var fill = new Fill({
            color: 'rgba(185, 107, 225, 0.4)'
        });
        var stroke = new Stroke({
            color: 'rgba(185, 107, 225, 1)',
            width: 2
        });

        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(185, 107, 225, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private Event(): Style[] {
        var fill = new Fill({
            color: 'rgba(241, 171, 0, 0.4)'
        });
        var stroke = new Stroke({
            color: 'rgba(241, 171, 0, 1)',
            width: 2
        });
        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(241, 171, 0, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private Designation(): Style[] {
        return this.CommonStyle('rgba(132, 71, 15,0.4)');
    }

    private ListedBuilding(): Style[] {
        var fill = new Fill({
            color: 'rgba(255, 0, 255, 0.4)'
        });
        var stroke = new Stroke({
            color: 'rgba(255, 0, 255, 1)',
            width: 2
        });
        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(255, 0, 255, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private ConservationArea(): Style[] {
        var fill = new Fill({
            color: 'rgba(27, 15, 220, 0.4)'
        });
        var stroke = new Stroke({
            color: 'rgba(27, 15, 220, 1)',
            width: 2
        });
        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(27, 15, 220, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private ScheduledMonument(): Style[] {
        var fill = new Fill({
            color: 'rgba(226, 0, 0, 0.4)'
        });
        var stroke = new Stroke({
            color: 'rgba(226, 0, 0, 1)',
            width: 2
        });

        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(226, 0, 0, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }
    
    private GardensAndDesignedLandscapes(): Style[] {
        var fill = new Fill({
            color: 'rgba(168, 0, 134, 0.4)'
        });
        var stroke = new Stroke({
            color: 'rgba(168, 0, 134, 1)',
            width: 2
        });

        return [new Style({
            image: new Circle({
                fill: new Fill({ color: 'rgba(168, 0, 134, 1)' }),
                stroke: new Stroke({ color: 'rgba(50, 50, 50, 1)', width: 1 }),
                radius: 4
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private Battlefields(): Style[] {
        // Generate a canvasPattern
        var canvas = document.createElement('canvas');
        var context = canvas.getContext('2d');
        var p = document.createElement('canvas');
        // Gradient and pattern are in canvas pixel space, so we adjust for the
        // renderer's pixel ratio
        var pixelRatio = olHas.DEVICE_PIXEL_RATIO;
        var pattern = (function () {
            canvas.width = 32 * pixelRatio;
            canvas.height = 16 * pixelRatio;
            // background
            context.fillStyle = "rgba(239, 26, 28, 0.1)";
            context.fillRect(0, 0, canvas.width, canvas.height);

            var x0 = 36;
            var x1 = -4;
            var y0 = -2;
            var y1 = 18;
            var offset = 32;

            context.strokeStyle = "rgba(239, 26, 28, 1)";
            context.lineWidth = 1;
            context.beginPath();
            context.moveTo(x0, y0);
            context.lineTo(x1, y1);
            context.moveTo(x0 - offset, y0);
            context.lineTo(x1 - offset, y1);
            context.moveTo(x0 + offset, y0);
            context.lineTo(x1 + offset, y1);
            context.stroke();

            context.fillStyle = context.createPattern(canvas, 'repeat');
            context.fillRect(0, 0, canvas.width, canvas.height);

            return context.createPattern(canvas, 'repeat');
        }());
        var fill = new Fill({
            color: 'rgba(255, 26, 28, 0.4)'
        });
        fill.setColor(pattern);

        var stroke = new Stroke({
            color: 'rgba(239, 26, 28, 1)',
            width: 2
        });

        return [new Style({
            image: new RegularShape({
                fill: new Fill({ color: 'rgba(239, 26, 28, 0.4)' }),
                stroke: stroke,
                points: 3,
                radius: 8,
                radius2: 4,
                angle: 0
            }),
            fill: fill,
            stroke: stroke
        })];
    }

    private Selected(): Style[] {
        let fill = new Fill({
            color: 'rgba(63, 211, 0, 0.6)'
        });

        return [
            new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            })
        ];
    }
    
    private MonumentSelected(): Style[] {
        return this.Selected();
    }

    private EventSelected(): Style[] {
        return this.Selected();
    }

    private DesignationSelected(): StyleFunction {
        return function (feature: Feature): Style {
            let fill = new Fill({
                color: 'rgba(63, 211, 0, 0.6)'
            });
            let style = new Style({
                image: new Circle({
                    fill: fill,
                    stroke: new Stroke({
                        color: 'rgba(76, 255, 0, 0.6)',
                        width: 5
                    }),
                    radius: 12
                }),
                fill: fill,
                stroke: new Stroke({
                    color: 'rgba(76, 255, 0, 0.8)',
                    width: 5
                })
            });

            return style;

        }
    }
}
